import { useMemo } from "react";
import { BrainCircuit, Sparkles, UserRound } from "lucide-react";

import { useApi } from "@/hooks/use-api";
import { useOrganizationMembers } from "@/hooks/use-organization-members";
import { AiSuggestions } from "./ai-suggestions";
import { SentimentBadge } from "./sentiment-badge";

interface Message {
  id: string;
  direction: "inbound" | "outbound";
  channel: string;
  body: string;
  senderType: "contact" | "agent" | "system";
  senderName: string;
  createdAt: string;
  isInternalNote?: boolean;
}

interface TeamMember {
  id: string;
  name: string | null;
  email: string;
  role: string;
  image: string | null;
}

interface AssignmentWorkload {
  assigneeId: string;
  openConversationCount: number;
  availability: "idle" | "active" | "busy";
}

type RoutingConditionType =
  | "channel"
  | "keyword"
  | "segment"
  | "tag"
  | "business_hours"
  | "skill"
  | "language"
  | "availability";

type RoutingAvailability = AssignmentWorkload["availability"] | "offline";

interface RoutingCondition {
  type: RoutingConditionType;
  operator: "eq" | "neq" | "contains" | "in";
  value: string | string[] | boolean;
}

interface RoutingTarget {
  userId: string;
  label?: string;
  skills?: string[];
  languages?: string[];
}

interface RoutingAction {
  type: "assign_user" | "assign_team" | "round_robin";
  userId?: string;
  teamId?: string;
  userPool?: string[];
  targets?: RoutingTarget[];
}

interface RoutingRule {
  id: string;
  name: string;
  priority: number;
  isActive: boolean;
  conditions: RoutingCondition[];
  action: RoutingAction;
  matchCount?: number;
}

interface SentimentResponse {
  sentiment: "positive" | "neutral" | "negative";
  confidence: number;
  aiGenerated: boolean;
}

interface ConversationAssistProps {
  conversationId: string;
  conversationChannel: string;
  messages: Message[];
  onSelectSuggestion: (text: string, meta: { suggestionIndex: number; aiGenerated: boolean }) => void;
  onRejectSuggestion?: (text: string, meta: { suggestionIndex: number; aiGenerated: boolean }) => void;
}

const SENTIMENT_SCORE_BY_LABEL = {
  positive: 0.9,
  neutral: 0.5,
  negative: 0.1,
} as const;

const AVAILABILITY_TONES: Record<AssignmentWorkload["availability"], string> = {
  idle: "bg-[var(--color-success)]/10 text-[var(--color-success)]",
  active: "bg-[var(--color-primary)]/10 text-[var(--color-primary)]",
  busy: "bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
};

function evaluateRoutingRules(
  rules: RoutingRule[],
  context: {
    channel: string;
    messageBody: string;
    contactTags: string[];
    contactSegments: string[];
    isBusinessHours: boolean;
    detectedLanguage?: string | null;
    requestedSkills?: string[];
    memberAvailability?: Record<string, RoutingAvailability>;
  },
) {
  for (const rule of rules) {
    if (!rule.isActive) continue;

    const allConditionsMet = rule.conditions.every((condition) =>
      evaluateCondition(condition, context, rule.action),
    );

    if (!allConditionsMet) continue;

    const eligibleUserIds = getEligibleUserIds(rule.action, context);
    const resolvedUserId = rule.action.type === "round_robin"
      ? eligibleUserIds[(rule.matchCount ?? 0) % Math.max(eligibleUserIds.length, 1)] ?? null
      : eligibleUserIds[0] ?? null;

    return {
      matched: true,
      ruleName: rule.name,
      resolvedUserId,
    };
  }

  return {
    matched: false,
    ruleName: null,
    resolvedUserId: null,
  };
}

function evaluateCondition(
  condition: RoutingCondition,
  context: {
    channel: string;
    messageBody: string;
    contactTags: string[];
    contactSegments: string[];
    isBusinessHours: boolean;
    detectedLanguage?: string | null;
    requestedSkills?: string[];
    memberAvailability?: Record<string, RoutingAvailability>;
  },
  action: RoutingAction,
): boolean {
  switch (condition.type) {
    case "channel":
      return condition.operator === "eq"
        ? context.channel === condition.value
        : context.channel !== condition.value;
    case "keyword": {
      const keywords = toStringArray(condition.value);
      const body = context.messageBody.toLowerCase();
      return keywords.some((keyword) => body.includes(keyword.toLowerCase()));
    }
    case "segment": {
      const segments = toStringArray(condition.value);
      return condition.operator === "in"
        ? segments.some((segment) => context.contactSegments.includes(segment))
        : !segments.some((segment) => context.contactSegments.includes(segment));
    }
    case "tag": {
      const tags = toStringArray(condition.value);
      return condition.operator === "in"
        ? tags.some((tag) => context.contactTags.includes(tag))
        : !tags.some((tag) => context.contactTags.includes(tag));
    }
    case "business_hours":
      return context.isBusinessHours === (condition.value === true);
    case "skill": {
      const requestedSkills = (context.requestedSkills ?? []).map(normalizeToken);
      const skills = toStringArray(condition.value).map(normalizeToken).filter(Boolean);
      return skills.length === 0 || skills.some((skill) => requestedSkills.includes(skill));
    }
    case "language": {
      const languages = toStringArray(condition.value).map((value) => value.toLowerCase());
      return languages.length === 0 || (context.detectedLanguage ? languages.includes(context.detectedLanguage.toLowerCase()) : false);
    }
    case "availability": {
      const required = toStringArray(condition.value);
      const candidateIds = getActionCandidateIds(action);
      const memberAvailability = context.memberAvailability ?? {};
      return candidateIds.some((candidateId) => required.includes(memberAvailability[candidateId] ?? "offline"));
    }
    default:
      return false;
  }
}

function getActionCandidateIds(action: RoutingAction): string[] {
  if (action.targets && action.targets.length > 0) {
    return action.targets.map((target) => target.userId);
  }
  if (action.userPool && action.userPool.length > 0) {
    return action.userPool;
  }
  return action.userId ? [action.userId] : [];
}

function getEligibleUserIds(
  action: RoutingAction,
  context: {
    detectedLanguage?: string | null;
    requestedSkills?: string[];
    memberAvailability?: Record<string, RoutingAvailability>;
  },
): string[] {
  const requestedSkills = (context.requestedSkills ?? []).map(normalizeToken);
  const detectedLanguage = context.detectedLanguage?.toLowerCase() ?? null;

  const targets: RoutingTarget[] = action.targets && action.targets.length > 0
    ? action.targets
    : getActionCandidateIds(action).map((userId) => ({ userId }));

  return targets.filter((target) => {
    const skills = (target.skills ?? []).map((skill: string) => normalizeToken(skill)).filter(Boolean);
    if (skills.length > 0 && requestedSkills.length > 0 && !skills.some((skill) => requestedSkills.includes(skill))) {
      return false;
    }

    const languages = (target.languages ?? []).map((value: string) => value.toLowerCase());
    if (languages.length > 0 && detectedLanguage && !languages.includes(detectedLanguage)) {
      return false;
    }

    const availability = context.memberAvailability?.[target.userId];
    return availability ? availability !== "offline" : true;
  }).map((target) => target.userId);
}

function collectSkillVocabulary(rules: RoutingRule[]): string[] {
  const skills = new Set<string>();

  for (const rule of rules) {
    for (const condition of rule.conditions) {
      if (condition.type !== "skill") {
        continue;
      }
      for (const skill of toStringArray(condition.value)) {
        const normalized = normalizeToken(skill);
        if (normalized) {
          skills.add(normalized);
        }
      }
    }

    for (const target of rule.action.targets ?? []) {
      for (const skill of target.skills ?? []) {
        const normalized = normalizeToken(skill);
        if (normalized) {
          skills.add(normalized);
        }
      }
    }
  }

  return Array.from(skills);
}

function inferRequestedSkills(messageBody: string, vocabulary: string[]): string[] {
  const haystack = messageBody.toLowerCase();
  return vocabulary.filter((skill) => {
    if (haystack.includes(skill)) {
      return true;
    }

    const parts = skill.split(/\s+/).filter(Boolean);
    return parts.length > 1 && parts.every((part) => haystack.includes(part));
  });
}

function detectLanguage(messageBody: string): string | null {
  const normalized = messageBody.toLowerCase();

  if (/[ăâîșşțţ]/.test(normalized) || /\b(salut|programare|mâine|vreau|ajutor)\b/.test(normalized)) {
    return "ro";
  }
  if (/[¿¡]/.test(normalized) || /\b(hola|gracias|precio|pedido|ayuda|mañana)\b/.test(normalized)) {
    return "es";
  }
  if (/\b(bonjour|merci|prix|commande|demain)\b/.test(normalized)) {
    return "fr";
  }
  if (/\b(hallo|danke|preis|bestellung|morgen)\b/.test(normalized)) {
    return "de";
  }
  if (/\b(ciao|grazie|prezzo|ordine|domani)\b/.test(normalized)) {
    return "it";
  }
  if (/\b(ol[aá]|obrigado|pre[cç]o|pedido|amanh[ãa])\b/.test(normalized)) {
    return "pt";
  }
  return normalized.trim().length === 0 ? null : "en";
}

function normalizeToken(value: string): string {
  return value.trim().toLowerCase();
}

function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => typeof entry === "string");
  }
  return typeof value === "string" ? [value] : [];
}

export function ConversationAssist({
  conversationId,
  conversationChannel,
  messages,
  onSelectSuggestion,
  onRejectSuggestion,
}: ConversationAssistProps) {
  const { data: sentiment, isLoading: sentimentLoading } = useApi<SentimentResponse>(
    conversationId ? `/v1/inbox/conversations/${conversationId}/sentiment` : "",
    { enabled: Boolean(conversationId) },
  );
  const { data: routingRules } = useApi<RoutingRule[]>("/v1/inbox-routing");
  const { data: members } = useOrganizationMembers();
  const { data: workload } = useApi<AssignmentWorkload[]>("/v1/inbox/assignment-options");

  const recommendation = useMemo(() => {
    if (!routingRules || routingRules.length === 0 || !members || !workload) {
      return null;
    }

    const conversationText = messages
      .filter((message) => message.direction === "inbound" && !message.isInternalNote)
      .map((message) => message.body.trim())
      .filter(Boolean)
      .join(" ");

    if (!conversationText) {
      return null;
    }

    const sortedRules = [...routingRules].filter((rule) => rule.isActive).sort((a, b) => a.priority - b.priority);
    const skillVocabulary = collectSkillVocabulary(sortedRules);
    const requestedSkills = inferRequestedSkills(conversationText, skillVocabulary);
    const detectedLanguage = detectLanguage(conversationText);
    const memberAvailability = Object.fromEntries(
      workload.map((entry) => [entry.assigneeId, entry.availability]),
    );

    const result = evaluateRoutingRules(sortedRules, {
      channel: conversationChannel,
      messageBody: conversationText,
      contactTags: [],
      contactSegments: [],
      isBusinessHours: true,
      detectedLanguage,
      requestedSkills,
      memberAvailability,
    });

    if (!result.matched || !result.resolvedUserId) {
      return null;
    }

    const member = members.find((entry) => entry.id === result.resolvedUserId);
    const memberLoad = workload.find((entry) => entry.assigneeId === result.resolvedUserId);

    return {
      assigneeLabel: member?.name ?? member?.email ?? result.resolvedUserId,
      availability: memberLoad?.availability ?? "idle",
      openConversationCount: memberLoad?.openConversationCount ?? 0,
      ruleName: result.ruleName,
      detectedLanguage,
      requestedSkills,
    };
  }, [conversationChannel, members, messages, routingRules, workload]);

  const sentimentScore = sentiment ? SENTIMENT_SCORE_BY_LABEL[sentiment.sentiment] : null;

  return (
    <div className="border-b border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-[14rem] flex-1 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-3">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
            <BrainCircuit className="h-3.5 w-3.5" />
            Conversation sentiment
          </div>
          <div className="mt-3 flex items-center gap-2">
            <SentimentBadge score={sentimentScore} />
            <span className="text-sm font-medium text-[var(--color-foreground)]">
              {sentimentLoading
                ? "Analyzing latest inbound messages..."
                : sentiment
                  ? `${Math.round(sentiment.confidence)}% confidence`
                  : "No sentiment signal yet"}
            </span>
          </div>
          {sentiment && (
            <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
              {sentiment.aiGenerated ? "AI-classified from the latest inbound messages." : "Fallback sentiment shown because AI was unavailable."}
            </p>
          )}
        </div>

        <div className="min-w-[16rem] flex-1 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-3">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
            <UserRound className="h-3.5 w-3.5" />
            Routing recommendation
          </div>
          {recommendation ? (
            <>
              <div className="mt-3 flex items-center gap-2">
                <p className="text-sm font-medium text-[var(--color-foreground)]">
                  Recommend {recommendation.assigneeLabel}
                </p>
                <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${AVAILABILITY_TONES[recommendation.availability]}`}>
                  {recommendation.availability}
                </span>
              </div>
              <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
                {recommendation.ruleName ? `Matched rule: ${recommendation.ruleName}. ` : ""}
                {recommendation.detectedLanguage ? `Language: ${recommendation.detectedLanguage.toUpperCase()}. ` : ""}
                {recommendation.requestedSkills.length > 0 ? `Skills: ${recommendation.requestedSkills.join(", ")}. ` : ""}
                Current load: {recommendation.openConversationCount} open conversations.
              </p>
            </>
          ) : (
            <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">
              No routing rule currently recommends an assignee for this conversation.
            </p>
          )}
        </div>
      </div>

      <div className="mt-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-3">
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
          <Sparkles className="h-3.5 w-3.5" />
          Reply suggestions
        </div>
        <AiSuggestions
          conversationId={conversationId}
          onSelectSuggestion={onSelectSuggestion}
          onRejectSuggestion={onRejectSuggestion}
        />
      </div>
    </div>
  );
}

export default ConversationAssist;
